<template>
    <div>
        <div class="form_flex_cont">
            <div class="rsv_group_tab">
                <button v-for="(department, index) in departments" :key="index" :class="{ btn: true, selected: selectedDepartment === index }" @click="selectDepartment(index)">
                    <span class="ellipsis">{{ department.title }}</span>
                </button>
            </div>
            <table class="table2_default mgt_10">
                <colgroup>
                    <col />
                    <col width="144" />
                    <col width="95" />
                </colgroup>
                <thead>
                    <tr>
                        <th>{{ $t('work_schedule.popup_operation_settings.label_staff_name') }}</th>
                        <th>{{ $t('work_schedule.popup_operation_settings.label_change_order') }}</th>
                        <th>{{ $t('common.delete') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(employee, index) in items" :key="employee._id">
                        <td>
                            <input type="text" ref="inputRefs" class="input_s w_full" v-model="employee.title" :placeholder="$t('work_schedule.popup_operation_settings.label_staff_name')" @focus="hideErrorText" />
                        </td>
                        <td>
                            <div class="btn_group">
                                <button class="btn btn_xs btn_lgray_line" :disabled="index === 0" @click="updateOrder('FIRST', index)">
                                    <v-icon>mdi-chevron-double-up</v-icon>
                                </button>
                                <button class="btn btn_xs btn_lgray_line" :disabled="index === 0" @click="updateOrder('PREVIOUS', index)">
                                    <v-icon>mdi-chevron-up</v-icon>
                                </button>
                                <button class="btn btn_xs btn_lgray_line" :disabled="index === items.length - 1" @click="updateOrder('NEXT', index)">
                                    <v-icon>mdi-chevron-down</v-icon>
                                </button>
                                <button class="btn btn_xs btn_lgray_line" :disabled="index === items.length - 1" @click="updateOrder('LAST', index)">
                                    <v-icon>mdi-chevron-double-down</v-icon>
                                </button>
                            </div>
                        </td>
                        <td class="txt_center">
                            <button class="btn btn_s btn_dgray" @click="removeItem(index)">－ {{ $t('common.delete') }}</button>
                        </td>
                    </tr>
                    <tr>
                        <td class="txt_center" colspan="3" v-if="isShowAddButton">
                            <button class="btn btn_s btn_blue_line mgl_auto" @click="action_AddItem">＋ {{ $t('common.add') }}</button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <small class="red_txt" v-if="isShowErrorText">{{ errorText }}</small>
        </div>
        <div class="btn_wrap" v-if="hasDepartments">
            <button class="btn btn_l btn_blue mgl_auto" @click="action_Save" :disabled="isLoading">{{ $t('common.save') }}</button>
        </div>
    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
import { mapActions } from 'vuex'
import ManagementMixin from '../ManagementMixin.js'
import { handleRefresh } from '@/v2/views/Main/V2ReservationManagement/sheet-refresh'
import { workerState, setSelectedDate } from '../../../worker-state'
import { useQueryClient } from '@tanstack/vue-query'
import { isApiErrorWithCode } from '@/apis/instances/check-api-response'
import { v2EmployeeFindQueryOptions } from '@/composables/queries/v2employee/use-v2-employee-find-query'
import { useV2EmployeeCreateMutation } from '@/composables/queries/v2employee/use-v2-employee-create-mutation'
import { useV2EmployeeUpdateMutation } from '@/composables/queries/v2employee/use-v2-employee-update-mutation'
import { useV2EmployeeRemoveMutation } from '@/composables/queries/v2employee/use-v2-employee-remove-mutation'
const moment = require('@/util/MPMoment')

export default {
    setup() {
        const queryClient = useQueryClient()
        const { mutateAsync: createEmployee } = useV2EmployeeCreateMutation()
        const { mutateAsync: updateEmployee } = useV2EmployeeUpdateMutation()
        const { mutateAsync: removeEmployee } = useV2EmployeeRemoveMutation()
        return { queryClient, createEmployee, updateEmployee, removeEmployee }
    },
    mixins: [ManagementMixin],
    props: {
        departments: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            selectedDepartment: 0,
            worker: workerState,
            employees: []
        }
    },
    watch: {
        departments: {
            handler() {
                this.selectedDepartment = 0
                this.requestFetchEmployeeList()
            },
            immediate: true
        }
    },
    methods: {
        ...mapActions(['setToast']),
        validateEmployees() {
            return this.validateItems('title', {
                empty: this.$t('work_schedule.popup_operation_settings.error_staff_name'),
                duplicate: this.$t('work_schedule.popup_operation_settings.error_same_staff_name'),
                noChange: this.$t('work_schedule.popup_operation_settings.error_no_change')
            })
        },
        selectDepartment(index) {
            if (this.selectedDepartment === index) return
            this.selectedDepartment = index
            this.requestFetchEmployeeList()
        },

        // MARK: - Network
        async requestFetchEmployeeList() {
            if (!this.departments.length) return

            try {
                let hospitalID = this.hospital.selectedHospital._id
                let result = await this.queryClient.fetchQuery(v2EmployeeFindQueryOptions({
                    hospitalID,
                    isRemoved: false,
                    way: 1,
                    key: 'sortNum',
                    departmentID: this.departments[this.selectedDepartment]._id
                }))

                this.employees = result ?? []

                this.items = deepCopy(this.employees)
                this.originalItems = deepCopy(this.items)
            } catch (err) {
                console.error('Error fetching employee list:', err)
            }
        },
        async handleCreateEmployees() {
            let createTasks = this.items.filter((emp) => !emp._id)

            for (let i = 0; i < createTasks.length; i++) {
                let emp = createTasks[i]
                try {
                    const created = await this.createEmployee({
                        title: emp.title,
                        hospitalID: this.hospital.selectedHospital._id,
                        departmentID: this.departments[this.selectedDepartment]._id,
                        sortNum: emp.sortNum
                    })
                    await this.updateEmployee({
                        _id: created._id,
                        sortNum: emp.sortNum
                    })
                } catch (err) {
                    // 기존 동작 미러링: 봉투 code != 0 이면 로그만 남기고 다음 항목 진행
                    if (!isApiErrorWithCode(err)) throw err
                    console.log(err)
                }
            }

            return Promise.all(createTasks)
        },
        async handleUpdateEmployees() {
            let updateTasks = this.items
                .filter((emp) => emp._id && !this.originalItems.some((orig) => orig._id === emp._id && orig.title === emp.title && orig.sortNum === emp.sortNum))
                .map((emp) =>
                    this.updateEmployee({
                        _id: emp._id,
                        title: emp.title,
                        hospitalID: this.hospital.selectedHospital._id,
                        departmentID: this.departments[this.selectedDepartment]._id,
                        sortNum: emp.sortNum
                    }).catch((err) => {
                        // 기존 동작 미러링: 봉투 에러는 무시하고 저장 성공 처리
                        if (!isApiErrorWithCode(err)) throw err
                    })
                )
            return Promise.all(updateTasks)
        },
        async handleRemoveEmployees() {
            let currentIds = this.items.map((emp) => emp._id)
            let deleteTasks = this.originalItems
                .filter((emp) => emp._id && !currentIds.includes(emp._id))
                .map((emp) =>
                    this.removeEmployee({ _id: emp._id }).catch((err) => {
                        // 기존 동작 미러링: 봉투 에러는 무시하고 저장 성공 처리
                        if (!isApiErrorWithCode(err)) throw err
                    })
                )
            return Promise.all(deleteTasks)
        },

        // MARK: - Actions
        action_AddItem() {
            this.items.push({ title: '' })
            this.$nextTick(() => {
                let inputs = this.$refs.inputRefs
                if (inputs && inputs.length > 0) {
                    inputs[inputs.length - 1].focus()
                }
            })
        },

        async action_Save() {
            if (!this.validateEmployees()) return

            try {
                this.isLoading = true
                await Promise.all([this.handleCreateEmployees(), this.handleUpdateEmployees(), this.handleRemoveEmployees()])

                // Employee 리스트 Refetch
                await this.requestFetchEmployeeList()
                // 구 ManagingDataController 재주입 제거 — 직원 mutation invalidation 이
                // useManagingWorkers 구독을 자동 재조회시킨다
                handleRefresh()
                setSelectedDate(moment(this.worker.selectedDate))
                this.setToast({
                    isShow: true,
                    text: this.$t('common.label_save_success')
                })
            } catch (err) {
                console.error('Error processing employee updates:', err)
                this.setToast({
                    isShow: true,
                    text: this.$t('common.label_save_error')
                })
            } finally {
                this.isLoading = false
            }
        }
    },
    computed: {
        noDepartments() {
            return !this.departments.length
        },
        hasDepartments() {
            return !!this.departments.length
        },
        isShowAddButton() {
            // employees 배열이 비어있지 않고, 선택된 부서에 대한 직원 데이터가 존재하며, 그 직원 데이터에 항목이 한 개라도 있는 경우
            if (this.items.length > 0 && this.items[this.selectedDepartment] && this.items[this.selectedDepartment].length > 0) {
                return false // 직원이 있으므로 '추가' 버튼을 숨김
            }
            return true // 그 외의 경우 '추가' 버튼을 표시
        }
    }
}
</script>
<style lang="scss" scoped></style>
